import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import { toast } from 'react-toastify';
import DeleteModal from '../../../../common/commonmodal/DeleteModal';

const ParkingTypeList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [parkingTypes, setParkingTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const fetchParkingTypes = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ParkingTypeMaster/GetAllParkingTypes`
            });

            // Handle both IsSuccess and isSuccess
            if (response.isSuccess || response.success || response.IsSuccess) {
                let apiData = response.data;
                let data = [];
                if (Array.isArray(apiData)) {
                    data = apiData;
                } else if (apiData && Array.isArray(apiData.data)) {
                    data = apiData.data;
                } else if (apiData && Array.isArray(apiData.ParkingTypeData)) {
                    data = apiData.ParkingTypeData;
                }
                setParkingTypes(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching parking types:", error);
            toast.error("Failed to fetch parking types.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParkingTypes();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "PARKING TYPE", key: "parkingType" },
        { label: "STATUS", key: "IsActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
            case "parkingType":
                return <span className="text-xs font-semibold text-[#1E293B]">{row[key] || "—"}</span>;
            case "IsActive":
                let statusColor = "bg-gray-100 text-gray-600";
                if (row.IsActive === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (row.IsActive === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {row.IsActive || "—"}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    // Filter by search query and status tab
    const safeData = Array.isArray(parkingTypes) ? parkingTypes : [];
    const filteredData = safeData.filter(item => {
        let matchesSearch = true;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            matchesSearch = (item.parkingType || "").toLowerCase().includes(query);
        }
        
        let matchesStatus = true;
        if (statusFilter) {
            matchesStatus = item.IsActive === statusFilter;
        }

        return matchesSearch && matchesStatus;
    });

    const paginatedData = filteredData.slice((currentPage - 1) * pageInfo.pageSize, currentPage * pageInfo.pageSize).map((item, idx) => ({
        ...item,
        id: item.parkingTypeID || `pt-${idx}`,
        no: (currentPage - 1) * pageInfo.pageSize + idx + 1
    }));

    const handleEdit = (row) => {
        navigate('/zentcomm/parking-management/parking-Type/form', { state: { recordData: row } });
    };

    const handleDeleteClick = (row) => {
        setRecordToDelete(row);
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        try {
            const res = await apiServices({
                method: "DELETE",
                url: `api/ParkingTypeMaster/RemoveParkingType/${recordToDelete.parkingTypeID}`
            });
            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success("Parking Category Deleted Successfully");
                fetchParkingTypes();
            } else {
                toast.error("Parking Category Deleted Failed");
            }
        } catch (error) {
            toast.error("Error deleting parking category");
        } finally {
            setIsDeleteOpen(false);
            setRecordToDelete(null);
        }
    };

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <SearchBar
                onSearch={setSearchQuery}
                placeholder="Search category..."
                className="w-full sm:w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection
                Navbar={true}
                mainHeading="Parking Category List"
                subHeading="Manage the different types of parking categories."
                primaryBtnText="Add New"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => {
                    navigate('/zentcomm/parking-management/parking-Type/form');
                }}
            />

            <CommonTable
                tableHeading="Parking Types"
                headerAction={headerAction}
                data={paginatedData}
                headers={tableHeaders}
                renderCell={renderCell}
                pagination={{
                    ...pageInfo,
                    pageNumber: currentPage,
                    totalRecords: filteredData.length,
                    totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                }}
                onPageChange={onPageChange}
                onLimitChange={handleLimitChange}
                loading={loading}
                showActionCol={true}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
            />

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onDelete={confirmDelete}
                title="Delete Parking Category"
                message="Are you sure you want to delete this parking category? This action cannot be undone."
            />
        </div>
    );
};

export default ParkingTypeList;
